import React from 'react';
import { CheckCircle2, XCircle, Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';

const verdictColor = (v) => {
  const n = (v || '').toLowerCase();
  if (n.includes('strong')) return 'text-emerald-300 bg-emerald-400/10 border-emerald-400/30';
  if (n.includes('good')) return 'text-cyan-300 bg-cyan-400/10 border-cyan-400/30';
  if (n.includes('marginal')) return 'text-amber-300 bg-amber-400/10 border-amber-400/30';
  return 'text-red-300 bg-red-400/10 border-red-400/30';
};

const clearanceColor = (v) => {
  const n = (v || '').toLowerCase();
  if (n.includes('meets') && !n.includes('not')) return 'text-emerald-300';
  if (n.includes('likely')) return 'text-cyan-300';
  if (n.includes('unclear')) return 'text-amber-300';
  return 'text-red-300';
};

const AnalysisPanel = ({ analysis }) => {
  if (!analysis?.result) return null;
  const r = analysis.result;
  return (
    <div className="bg-[#070b15] border border-cyan-400/10 rounded-lg p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-baseline gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span className="text-3xl font-bold text-white">{r.score}</span>
          <span className="text-slate-500 text-sm">/100</span>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${verdictColor(r.verdict)}`}>{r.verdict}</span>
        <span className={`text-xs inline-flex items-center gap-1 ${clearanceColor(r.clearance_match)}`}>
          <ShieldCheck className="w-3.5 h-3.5" /> Clearance: {r.clearance_match}
        </span>
        <span className="ml-auto text-[10px] text-slate-500">Analyzed by {analysis.model} &middot; {new Date(analysis.analyzed_at).toLocaleString()}</span>
      </div>

      <p className="text-sm text-slate-300 leading-relaxed">{r.summary}</p>

      <div className="grid md:grid-cols-2 gap-5">
        {r.strengths?.length > 0 && (
          <div>
            <p className="text-xs tracking-wider text-emerald-300 uppercase mb-2">Strengths</p>
            <ul className="space-y-1.5">{r.strengths.map(s => <li key={s} className="text-sm text-slate-300 flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-1" />{s}</li>)}</ul>
          </div>
        )}
        {r.gaps?.length > 0 && (
          <div>
            <p className="text-xs tracking-wider text-amber-300 uppercase mb-2">Gaps</p>
            <ul className="space-y-1.5">{r.gaps.map(s => <li key={s} className="text-sm text-slate-300 flex gap-2"><XCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-1" />{s}</li>)}</ul>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {(r.matched_requirements?.length > 0 || r.missing_requirements?.length > 0) && (
          <div>
            <p className="text-xs tracking-wider text-cyan-300 uppercase mb-2">Requirements</p>
            {r.matched_requirements?.map(s => <div key={'m'+s} className="text-xs text-slate-300 flex gap-2 mb-1"><CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" /><span className="line-through-none">{s}</span></div>)}
            {r.missing_requirements?.map(s => <div key={'u'+s} className="text-xs text-slate-500 flex gap-2 mb-1"><XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" /><span>{s}</span></div>)}
          </div>
        )}
        {(r.matched_certifications?.length > 0 || r.missing_certifications?.length > 0) && (
          <div>
            <p className="text-xs tracking-wider text-cyan-300 uppercase mb-2">Certifications</p>
            {r.matched_certifications?.map(s => <div key={'mc'+s} className="text-xs text-slate-300 flex gap-2 mb-1"><CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />{s}</div>)}
            {r.missing_certifications?.map(s => <div key={'uc'+s} className="text-xs text-slate-500 flex gap-2 mb-1"><XCircle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />{s}</div>)}
          </div>
        )}
      </div>

      {r.alternative_matches?.length > 0 && (
        <div>
          <p className="text-xs tracking-wider text-orange-300 uppercase mb-2 inline-flex items-center gap-1"><ArrowRight className="w-3.5 h-3.5" /> Better fits found in other positions</p>
          <div className="space-y-2">
            {r.alternative_matches.map(a => (
              <div key={a.job_number + a.title} className="rounded-md border border-orange-400/15 bg-orange-400/5 px-3 py-2 flex items-start gap-3">
                <span className="text-xl font-bold text-orange-300 leading-none mt-0.5">{a.score}</span>
                <div className="min-w-0">
                  <p className="text-sm text-white">{a.title} <span className="font-mono text-xs text-slate-500">#{a.job_number}</span></p>
                  <p className="text-xs text-slate-400">{a.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisPanel;
